import { Injectable } from '@angular/core';

/**
 * Service để quản lý preview data tạm thời khi preview từ màn create/edit
 * Data được lưu trong sessionStorage và sẽ tự động clear khi không cần thiết
 */
@Injectable({
  providedIn: 'root'
})
export class PreviewTempDataService {
  private readonly STORAGE_KEY_PREFIX = 'preview_temp_data_';

  /**
   * Lưu preview data tạm thời cho một digital memory log
   * @param digitalLogId ID của digital memory log
   * @param data Data cần lưu
   */
  setTempPreviewData(digitalLogId: string, data: any): void {
    if (!digitalLogId || !data) {
      return;
    }

    const key = this.getStorageKey(digitalLogId);
    try {
      const dataToStore = {
        data: data,
        timestamp: Date.now(),
        digitalLogId: digitalLogId
      };
      sessionStorage.setItem(key, JSON.stringify(dataToStore));
    } catch (error) {
      console.error('Error saving temp preview data:', error);
    }
  }

  /**
   * Lấy preview data tạm thời
   * @param digitalLogId ID của digital memory log
   * @returns Preview data hoặc null nếu không tồn tại
   */
  getTempPreviewData(digitalLogId: string): any | null {
    if (!digitalLogId) {
      return null;
    }

    const key = this.getStorageKey(digitalLogId);
    try {
      const stored = sessionStorage.getItem(key);
      if (!stored) {
        return null;
      }

      const parsed = JSON.parse(stored);
      // Kiểm tra timestamp để đảm bảo data không quá cũ (24 giờ)
      const maxAge = 24 * 60 * 60 * 1000; // 24 hours
      if (Date.now() - parsed.timestamp > maxAge) {
        this.clearTempPreviewData(digitalLogId);
        return null;
      }

      return parsed.data;
    } catch (error) {
      console.error('Error getting temp preview data:', error);
      return null;
    }
  }

  /**
   * Xóa preview data tạm thời
   * @param digitalLogId ID của digital memory log
   */
  clearTempPreviewData(digitalLogId: string): void {
    if (!digitalLogId) {
      return;
    }

    const key = this.getStorageKey(digitalLogId);
    try {
      sessionStorage.removeItem(key);
    } catch (error) {
      console.error('Error clearing temp preview data:', error);
    }
  }

  /**
   * Xóa tất cả preview data tạm thời
   */
  clearAllTempPreviewData(): void {
    try {
      const keysToRemove: string[] = [];
      for (let i = 0; i < sessionStorage.length; i++) {
        const key = sessionStorage.key(i);
        if (key && key.startsWith(this.STORAGE_KEY_PREFIX)) {
          keysToRemove.push(key);
        }
      }
      keysToRemove.forEach(key => sessionStorage.removeItem(key));
    } catch (error) {
      console.error('Error clearing all temp preview data:', error);
    }
  }

  /**
   * Kiểm tra xem có temp preview data cho digitalLogId không
   * @param digitalLogId ID của digital memory log
   * @returns true nếu có data, false nếu không
   */
  hasTempPreviewData(digitalLogId: string): boolean {
    if (!digitalLogId) {
      return false;
    }

    const key = this.getStorageKey(digitalLogId);
    return sessionStorage.getItem(key) !== null;
  }

  /**
   * Tạo storage key từ digitalLogId
   * @param digitalLogId ID của digital memory log
   * @returns Storage key
   */
  private getStorageKey(digitalLogId: string): string {
    return `${this.STORAGE_KEY_PREFIX}${digitalLogId}`;
  }
}


